import React, { useState, useMemo } from "react";
import {
  Box,
  Button,
  Typography,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Menu,
  MenuItem,
  Chip,
} from "@mui/material";
import { useApplicationUser, UserDto } from "../../../hooks/useApplicationUser";
import { useAuth } from "../../../contexts/AuthContext";
import MoreVertIcon from "@mui/icons-material/MoreVert";
import { useTranslation } from "react-i18next";
import AddIcon from "@mui/icons-material/Add";
import { useTenantHeader } from "../../../hooks/useTenantHeader";
import ApplicationUserModal from "components/Modals/ApplicationUserModal";
import EnhancedTable from "../../../components/common/EnhancedTable";

/**
 * ApplicationUserManagement Component
 * 
 * Manages application users - allows school admins to create users
 * from submitted applications or manually create new users.
 */
export default function ApplicationUserManagement() {
  const { t } = useTranslation();
  const { user, isSchoolAdmin } = useAuth();
  const { tenantLoading } = useTenantHeader();

  // UI state
  const [modalOpen, setModalOpen] = useState(false);
  const [isEdit, setIsEdit] = useState(false);
  const [currentUser, setCurrentUser] = useState<UserDto | undefined>(undefined);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [userToDelete, setUserToDelete] = useState<number | null>(null);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const [selectedRow, setSelectedRow] = useState<any>(null);

  // API hooks
  const { 
    applicationUsers, 
    isLoading, 
    isFetching,
    createUser, 
    isCreating,
    updateUser,
    isUpdating,
    deleteUser,
    isDeleting,
    refetch 
  } = useApplicationUser();

  // Handle form submit
  const handleSubmit = (formData: UserDto) => {
    const userData: UserDto = {
      ...formData,
      schoolId: user?.schoolId || 0,
    };

    if (isEdit) {
      updateUser(userData, {
        onSuccess: () => {
          setModalOpen(false);
          setCurrentUser(undefined);
          setIsEdit(false);
        }
      });
    } else {
      createUser(userData, {
        onSuccess: () => {
          setModalOpen(false);
          setCurrentUser(undefined);
          setIsEdit(false);
        }
      });
    }
  };

  // Handle open add modal
  const handleOpenAddModal = () => {
    setIsEdit(false);
    setCurrentUser(undefined);
    setModalOpen(true);
  };

  // Handle open edit modal
  const handleOpenEditModal = (userData: UserDto) => {
    setIsEdit(true);
    setCurrentUser(userData);
    setModalOpen(true);
  };

  // Handle close modal
  const handleCloseModal = () => {
    setModalOpen(false);
    setCurrentUser(undefined);
    setIsEdit(false);
  };

  // Handle delete user
  const handleDeleteUser = () => {
    if (userToDelete && selectedRow?.email) {
      deleteUser(selectedRow.email, {
        onSuccess: () => {
          setDeleteDialogOpen(false);
          setUserToDelete(null);
          setSelectedRow(null);
        }
      });
    }
  };

  // Define columns for EnhancedTable
  const columns = useMemo(() => [
    {
      id: 'username',
      label: t('username'),
      align: 'left' as const,
    },
    {
      id: 'email',
      label: t('email'),
      align: 'left' as const,
    },
    {
      id: 'status',
      label: t('status'),
      align: 'center' as const,
      format: (value: boolean) => (
        <Chip
          label={value ? t("active") : t("inactive")}
          size="small"
          color={value ? "success" : "error"}
        />
      ),
    },
  ], [t]);

  // Loading gate
  if (tenantLoading || isLoading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="100vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <>
      <Box sx={{ maxWidth: 1500, mx: "auto", mt: 2 }}>
        {/* Header */}
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            mb: 2,
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
            <Typography variant="h5" fontWeight="bold">
              {t("application_user_management")}
            </Typography>
            {isFetching && (
              <CircularProgress size={20} />
            )}
          </Box>

          {isSchoolAdmin() && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              disabled={isCreating || isUpdating || isDeleting}
              sx={{
                backgroundColor: "#3092e3",
                "&:hover": { backgroundColor: "primary.main" },
              }}
              onClick={handleOpenAddModal}
            >
              {isCreating ? t("creating") : t("create_application_user")}
            </Button>
          )}
        </Box>

        {/* EnhancedTable */}
        <EnhancedTable
          columns={columns}
          data={applicationUsers}
          searchFields={['username', 'email']}
          actions={isSchoolAdmin() ? (row) => (
            <IconButton
              onClick={(e) => {
                setAnchorEl(e.currentTarget);
                setSelectedRow(row);
              }}
              disabled={isCreating || isUpdating || isDeleting}
            >
              <MoreVertIcon />
            </IconButton>
          ) : undefined}
          cardProps={{
            elevation: 0,
            sx: { 
              border: '1px solid #e0e0e0',
              '& .MuiCardContent-root': { p: 2 }
            }
          }}
        />
      </Box>

      {/* Application User Modal */}
      <ApplicationUserModal
        open={modalOpen}
        handleClose={handleCloseModal}
        initialData={currentUser}
        onSubmit={handleSubmit}
        isEdit={isEdit}
      />

      {/* Action Menu */}
      <Menu anchorEl={anchorEl} open={Boolean(anchorEl)} onClose={() => setAnchorEl(null)}>
        <MenuItem
          onClick={() => {
            if (selectedRow) {
              handleOpenEditModal(selectedRow);
            }
            setAnchorEl(null);
          }}
          disabled={isUpdating || isCreating || isDeleting}
        >
          {isUpdating ? t("updating") : t("edit")}
        </MenuItem>
        <MenuItem
          onClick={() => {
            if (selectedRow?.id) {
              setUserToDelete(selectedRow.id);
              setDeleteDialogOpen(true);
            }
            setAnchorEl(null);
          }}
          disabled={isUpdating || isCreating || isDeleting}
          sx={{ color: "error.main" }}
        >
          {isDeleting ? t("deleting") : t("delete")}
        </MenuItem>
      </Menu>

      {/* Delete Dialog */}
      <Dialog open={deleteDialogOpen} onClose={() => setDeleteDialogOpen(false)}>
        <DialogTitle>{t("confirm_deletion")}</DialogTitle>
        <DialogContent>
          <DialogContentText>
            {t("are_you_sure_you_want_to_delete_this_user")}
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setUserToDelete(null);
              setSelectedRow(null);
            }}
            disabled={isDeleting}
            sx={{ backgroundColor: "#3092e3", "&:hover": { backgroundColor: "primary.main" } }}
          >
            {t("cancel")}
          </Button>
          <Button
            onClick={handleDeleteUser}
            disabled={isDeleting}
            sx={{
              backgroundColor: "#d32f2f",
              color: "white",
              "&:hover": { backgroundColor: "#b71c1c" },
            }}
          >
            {isDeleting ? (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <CircularProgress size={16} color="inherit" />
                {t("deleting")}
              </Box>
            ) : (
              t("delete")
            )}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

